// S5 · Step 3 — Plant map (`/onboarding/plant`, Module 02 §4 S5, §3.4), rebuilt
// as the design-system §3.7 structure editor (UX-1, D-107 — reference screen
// 02). The BEHAVIOR is the certified module-02 machinery unchanged: the draft
// lives in `onboarding_step_states.payload`, is saved through the shared
// `SaveContinueBar` (PUT payload + 409 STALE_PAYLOAD), and is committed through
// `POST /v1/onboarding/plant-map/commit`. This is a comprehension + register
// redesign.
//
// The five states (§UX-1 — "kills 'no clue if the mapping works'"):
//  1. EMPTY DRAFT — one claim on screen: a directing empty state with the ONE
//     primary "Add the first facility"; a committed plant instead offers to
//     load itself into the draft. No commit surface, no zero-count panels.
//  2. DRAFTING — the tree with draft chips, ghost add-rows, the properties
//     panel for the selection, the save bar's saved/unsaved status, and the
//     commit bar's LIVE plain-English sentence.
//  3. PRE-COMMIT — sentence non-empty ⇒ Commit enabled; "Validated · no
//     conflicts" or the blocking reason inline.
//  4. COMMIT SUCCESS — ONE inline banner (§UX-1: "toast-scale, not a
//     celebration"): it names every created node as a link that selects the
//     real committed row, carries the setup chip (the checklist re-evaluated
//     for real), states the outcome FROM THE SERVER'S 200 BODY, and hands over
//     to the next step. No toast, no second panel: the screen used to announce
//     the same commit three times and, worse, the banner's claim came from the
//     client's pre-commit prediction while the panel beneath it came from the
//     server, so one screen carried "Committed 1 new nodes" over "Updated 1 ·
//     archived 0" (surface review F2/F6). The step payload is saved in the same
//     breath, so the footer cannot read "You have unsaved changes." under a
//     banner that says the plant is committed (F8).
//  5. BIND CHECKLIST — per-step bound/unbound rows with one summary line
//     (bindings-panel.tsx), plus the People panel; both feed go-live checks.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Banner } from "../../../components/toast";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { StatusChip } from "../../../components/status-chip";
import { CardSkeleton, ErrorState } from "../../../components/states";
import { requestIdOf } from "../../../lib/request";
import { SaveContinueBar } from "../components/save-continue-bar";
import { StepContainer } from "../components/step-container";
import {
  useEvaluateChecklist,
  useInvites,
  useOnboardingSession,
  useRoles,
  useRoutingStepBindings,
  useSaveStepPayload,
} from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { useTerm, toTerminologyLocale } from "../hooks/terminology";
import { isWizardFrozen, stepStateOf, WIZARD_STEPS } from "../lib/wizard-steps";
import { BindingsPanel } from "./plant-map/bindings-panel";
import {
  CommitPanel,
  CommitResult,
  DeferredNote,
  type CommitSuccessInfo,
} from "./plant-map/commit-panel";
import { PeoplePanel } from "./plant-map/people-panel";
import {
  adoptCommittedNames,
  diffPlan,
  draftFromExisting,
  draftProblems,
  emptyFacility,
  isCommittable,
  isEmptyDraft,
  parseStepPayload,
  toStepPayload,
  type PlantDraft,
  type PlantNodeRef,
} from "./plant-map/plant-draft";
import {
  useExistingPlantTree,
  useRoutingStepTemplates,
  useWorkCenterTemplates,
} from "./plant-map/plant-queries";
import {
  browserTimezone,
  committedIndex,
  facilityUidOf,
  findNode,
  NodeProperties,
  PlantTree,
  uidOfFieldId,
  type EditorContext,
} from "./plant-map/tree-editor";
import "./plant-map/plant-map.css";

type S = components["schemas"];

const STEP_NO = 3;

/** Scroll to and focus a draft field by its DOM id (`${uid}-code`). The field
 * lives in the properties panel, which renders on the next frame after the
 * owning node is selected. */
function focusField(fieldId: string): void {
  requestAnimationFrame(() => {
    const el = document.getElementById(fieldId);
    if (el === null) return;
    el.scrollIntoView({ block: "center" });
    el.focus();
  });
}

/** Phone-first collapse: the tree opens collapsed at ≤768 px (the walk expands
 * a facility to reach its work) and expanded on desktop, like the reference. */
function defaultExpandedForViewport(): boolean {
  try {
    return !window.matchMedia("(max-width: 768px)").matches;
  } catch {
    return true;
  }
}

/** The §UX-1 state-4 record: what the last commit did, held until dismissed. */
interface LastCommit {
  readonly createdNodes: readonly PlantNodeRef[];
  readonly restoredNodes: readonly PlantNodeRef[];
  readonly response: S["PlantMapCommitResponse"];
  readonly scoreBefore: number | null;
}

/**
 * The nodes the banner may NAME as created, corroborated against the 200 body.
 *
 * The names come from the client's diff (the wire response carries counts, not
 * identities), so they are only shown when the server's own created total says
 * the same thing. If the two ever disagree the banner falls back to the
 * server's counts alone — the screen is then unable to make a claim the commit
 * did not back, which is the whole point of F2's fix.
 */
function corroborated(
  nodes: readonly PlantNodeRef[],
  serverCount: number,
): readonly PlantNodeRef[] {
  return nodes.length > 0 && nodes.length === serverCount ? nodes : [];
}

export function PlantMapScreen() {
  const { t, i18n } = useTranslation();
  // `isLoading` joins the reveal gate below: terminology decides the WORDS in
  // every panel of the content region (`nouns`), and a noun that arrives after
  // first paint re-wraps the copy it sits in — the same §5 defect as a panel
  // that pops in, just spelled in text. It costs the gate nothing: this read is
  // issued in the screen's FIRST wave, beside the org and pack reads whose
  // second wave the gate already waits for (D-154's mount-cost measurement).
  const { term, isLoading: termsPending } = useTerm();
  const locale = toTerminologyLocale(i18n.language);
  const navigate = useNavigate();
  const perms = useOnboardingPermissions();
  const session = useOnboardingSession();
  const existing = useExistingPlantTree();
  const templates = useWorkCenterTemplates(locale);
  const stepTemplates = useRoutingStepTemplates(locale);
  const evaluate = useEvaluateChecklist();
  const saveStep = useSaveStepPayload(STEP_NO);
  // §5 render-once, applied to CONTENT: the bind checklist and the People panel
  // decide their own layout from reads (bindings, invites, roles). Their query
  // keys are warmed HERE — same cache entries the panels use — so the whole
  // region below gates once and reveals atomically instead of the two Cards
  // reflowing as their reads land (measured CLS 0.013 before this gate).
  const firstFacilityId = existing.tree.facilities.find((f) => f.status === "active")?.id ?? "";
  const bindingsWarm = useRoutingStepBindings(firstFacilityId);
  const invitesWarm = useInvites({ status: "pending" });
  const rolesWarm = useRoles();

  const [draft, setDraft] = useState<PlantDraft>({ facilities: [] });
  /** The draft as it was before the last removal, so it can be restored. */
  const [undo, setUndo] = useState<{ draft: PlantDraft; label: string } | null>(null);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [defaultExpanded] = useState(defaultExpandedForViewport);
  /** §UX-1 state 5 opens with the editor on desktop and behind one disclosure
   * row at phone widths. Read ONCE at mount (lazy initializer), like
   * `defaultExpanded` — a media query re-read on every render would slam a
   * panel shut mid-edit when a phone rotates. */
  const [followUpOpen, setFollowUpOpen] = useState(defaultExpandedForViewport);
  const [lastCommit, setLastCommit] = useState<LastCommit | null>(null);
  const [scoreAfter, setScoreAfter] = useState<number | null>(null);
  // The step payload is seeded ONCE per loaded version. Re-seeding on every
  // session refetch (the save bar invalidates it) would throw away edits made
  // while a request was in flight.
  const seededFrom = useRef<string | null>(null);
  /** Set by a successful commit; cleared when the refetched tree is adopted. */
  const adoptAfterCommit = useRef(false);

  const step = session.data ? stepStateOf(session.data.steps, STEP_NO) : undefined;
  const stepId = step?.id ?? null;

  useEffect(() => {
    if (step === undefined || seededFrom.current === stepId) return;
    seededFrom.current = stepId;
    setDraft(parseStepPayload(step.payload));
  }, [step, stepId]);

  // ADOPT THE COMMIT'S OWN ANSWER (§UX-1 state 4 → state 2). A commit merges
  // server-side and normalises what it stores, so the tree it leaves behind can
  // legitimately differ from the bundle this draft sent — one locale richer, or
  // one trailing space poorer. Re-basing the draft's bundles on the refetched
  // tree is what makes "the draft now equals the committed plant" TRUE rather
  // than hoped for: without it the draft argues with the server forever, the
  // diff keeps reporting an edit that will never be applied, and the commit
  // surface can never go quiet. Names the user typed are untouched.
  const committedFacilities = existing.tree.facilities;
  const committedWorkCenters = existing.tree.workCenters;
  useEffect(() => {
    if (!adoptAfterCommit.current) return;
    adoptAfterCommit.current = false;
    setDraft((prev) =>
      adoptCommittedNames(prev, {
        facilities: committedFacilities,
        workCenters: committedWorkCenters,
      }),
    );
  }, [committedFacilities, committedWorkCenters]);

  if (session.isPending) {
    return (
      <StepContainer
        stepNo={STEP_NO}
        titleKey="onboarding.steps.plant.title"
        blurbKey="onboarding.steps.plant.blurb"
      >
        <CardSkeleton lines={6} label={t("onboarding.state.loading")} />
      </StepContainer>
    );
  }

  if (session.error !== null) {
    return (
      <StepContainer
        stepNo={STEP_NO}
        titleKey="onboarding.steps.plant.title"
        blurbKey="onboarding.steps.plant.blurb"
      >
        <ErrorState
          title={t("onboarding.state.errorTitle")}
          detail={t("onboarding.state.errorDetail")}
          retryLabel={t("onboarding.state.retry")}
          onRetry={() => void session.refetch()}
          requestId={requestIdOf(session.error)}
          requestIdLabel={t("onboarding.state.requestId")}
        />
      </StepContainer>
    );
  }

  const frozen = isWizardFrozen(session.data.session);
  const canEdit = perms.has("onboarding.manage_wizard") && !frozen;
  const problems = draftProblems(draft);
  const ctx: EditorContext = {
    readOnly: !canEdit,
    templates: templates.options,
    templatesUnavailable: templates.noPack,
    problems,
  };

  const errors = problems.filter((p) => p.severity === "error");
  const warnings = problems.filter((p) => p.severity === "warning");
  const diff = diffPlan(draft, existing.tree, locale);
  const committed = committedIndex(existing.tree);
  const nouns = {
    facility: term("facility"),
    department: term("department"),
    workCenter: term("work_center"),
    machine: term("machine"),
  };
  // ACTIVE rows only: the committed-tree read now carries archived rows too (so
  // the diff can tell a restore from a create), and a plant of nothing but
  // archived facilities is not a plant to offer loading.
  const hasCommittedTree = existing.tree.facilities.some((f) => f.status === "active");
  const selected = findNode(draft, selectedUid);
  const draftNodeCount = draft.facilities.reduce(
    (n, f) =>
      n +
      1 +
      f.storage_locations.length +
      f.departments.reduce((m, d) => m + 1 + d.work_centers.length, 0),
    0,
  );
  const draftOnlyCount =
    diff.create.facilities + diff.create.departments + diff.create.work_centers;

  const setFacilities = (facilities: PlantDraft["facilities"]) => {
    setDraft({ facilities });
  };

  /** Select a node and make sure its ancestors are open, so it is on screen. */
  function selectNode(uid: string): void {
    const facilityUid = facilityUidOf(draft, uid);
    setExpanded((prev) => {
      const next = { ...prev };
      if (facilityUid !== undefined) next[facilityUid] = true;
      const node = findNode(draft, uid);
      if (node?.kind === "work_center") next[node.department.uid] = true;
      return next;
    });
    setSelectedUid(uid);
  }

  /** A problem names a field; the field lives in the properties panel, so the
   * owning node is selected first and the input focused on the next frame. */
  function focusProblem(fieldId: string): void {
    selectNode(uidOfFieldId(fieldId));
    focusField(fieldId);
  }

  /** State-4 banner link: select the (now committed) node in the tree. */
  function selectByPath(path: string): void {
    for (const f of draft.facilities) {
      if (f.code.trim() === path) {
        selectNode(f.uid);
        return;
      }
      for (const d of f.departments) {
        if (`${f.code.trim()}/${d.code.trim()}` === path) {
          selectNode(d.uid);
          return;
        }
        for (const w of d.work_centers) {
          if (`${f.code.trim()}/${d.code.trim()}/${w.code.trim()}` === path) {
            selectNode(w.uid);
            return;
          }
        }
      }
    }
  }

  function onCommitSuccess(info: CommitSuccessInfo): void {
    const scoreBefore = session.data?.checklist_summary.score ?? null;
    setLastCommit({ ...info, scoreBefore });
    setScoreAfter(null);
    // The tree refetch this commit just triggered is the authority on what was
    // stored; adopt it the moment it lands (see the effect above).
    adoptAfterCommit.current = true;
    // ONE announcement (§UX-1 state 4). No toast: the banner below IS the
    // confirmation, and the reference's own rule for this screen is
    // "toast-scale, not a celebration".
    //
    // The draft the user just committed is now the plant of record, so it is
    // persisted as the step payload in the same breath. Without this the footer
    // read "You have unsaved changes." directly under a banner saying the plant
    // was committed, and leaving the step raised a data-loss dialog about work
    // that had, in fact, just been written to the org tree (surface review F8).
    // The save goes through the same mutation the footer's Save uses, so the
    // session refetch it triggers moves the save bar's baseline and the footer
    // flips to "Saved" on its own. A 409 from a concurrent editor leaves the
    // footer honestly dirty — the commit still stands.
    if (canEdit) {
      saveStep.mutate({
        payload: toStepPayload(draft, locale),
        ...(step?.updated_at !== undefined ? { updated_at: step.updated_at } : {}),
      });
    }
    // The completeness delta ticks for real: re-evaluate the checklist (the
    // same endpoint S8's button calls), then show the setup chip. Best-effort —
    // a role that cannot evaluate simply gets no chip.
    if (canEdit) {
      evaluate.mutate(undefined, {
        onSuccess: (data) => {
          setScoreAfter(data.score);
        },
      });
    }
  }

  /* The one atomic-reveal gate for the content region (§5): the committed
   * tree decides WHICH state renders; the pack templates decide the bind
   * checklist's rows; invites/roles decide the People panel. Fail states
   * bypass the gate — an error is a real state, not an eternal skeleton. */
  const contentPending =
    (existing.isPending && !existing.isError && !existing.forbidden) ||
    templates.isPending ||
    stepTemplates.isPending ||
    termsPending ||
    // TanStack v5 results are discriminated unions: `isPending` already means
    // not-yet-settled (a failed read drops isPending to false and the panel
    // renders its own error state) — never an eternal skeleton.
    (firstFacilityId !== "" && bindingsWarm.isPending) ||
    invitesWarm.isPending ||
    rolesWarm.isPending;

  const nextStep = WIZARD_STEPS.find((d) => d.stepNo === STEP_NO + 1);
  /* The state-4 hand-over primary: built as a value so the route stays a
   * narrowed const for the click closure. */
  let nextStepCta: ReactNode = null;
  if (nextStep?.to !== undefined && nextStep.to !== null && !frozen) {
    const to = nextStep.to;
    const stepLabel = t(nextStep.titleKey);
    nextStepCta = (
      <div className="plant-success-next">
        <Button
          variant="primary"
          data-testid="plant-continue-next"
          onClick={() => {
            void navigate({ to });
          }}
        >
          {t("onboarding.plant.continueNext", { step: stepLabel })}
        </Button>
      </div>
    );
  }
  const delta =
    lastCommit !== null && scoreAfter !== null && lastCommit.scoreBefore !== null
      ? scoreAfter - lastCommit.scoreBefore
      : null;
  /* The reference's banner carries "Setup +15% → 49%" — the proof that the
   * mapping moved the tenant forward. It used to render ONLY when delta > 0,
   * which on a tenant that already has a mapped plant is never (creating one
   * more work center flips no checklist item), so the chip the whole state
   * exists to show had never once appeared (surface review F7). The re-evaluated
   * score is worth stating either way: with the delta when it moved, as the
   * plain standing figure when it did not. */
  /* The banner's claims, resolved once: the server's created total, and the
   * name lists it corroborates (see `corroborated`). */
  const serverCreated =
    lastCommit === null
      ? 0
      : lastCommit.response.created.facilities +
        lastCommit.response.created.departments +
        lastCommit.response.created.work_centers;
  const createdNamed =
    lastCommit === null ? [] : corroborated(lastCommit.createdNodes, serverCreated);
  const restoredNamed =
    lastCommit === null
      ? []
      : corroborated(
          lastCommit.restoredNodes,
          Math.min(lastCommit.restoredNodes.length, lastCommit.response.updated),
        );
  const setupChip =
    scoreAfter === null ? null : (
      <StatusChip
        variant={delta !== null && delta > 0 ? "ok" : "neutral"}
        data-testid="plant-setup-delta"
      >
        {delta !== null && delta > 0
          ? t("onboarding.plant.setupDelta", { delta, score: scoreAfter })
          : t("onboarding.plant.setupScore", { score: scoreAfter })}
      </StatusChip>
    );

  return (
    <StepContainer
      stepNo={STEP_NO}
      titleKey="onboarding.steps.plant.title"
      blurbKey="onboarding.steps.plant.blurb"
      footer={
        /* §5: the save bar mounts WITH the revealed content — mounted under
         * the gate skeleton it would be displaced when the content lands. */
        contentPending ? undefined : (
          <SaveContinueBar
            stepNo={STEP_NO}
            getPayload={() => toStepPayload(draft, locale)}
            updatedAt={step?.updated_at}
            // The DERIVED scratch — the same parse→serialize round trip the
            // seeding effect performs — so a never-saved step (`{}` payload,
            // seeded as `{facilities: []}`) compares clean until a real edit
            // (§3.8).
            loadedPayload={
              step === undefined ? undefined : toStepPayload(parseStepPayload(step.payload), locale)
            }
            onStale={(serverStep) => {
              // Somebody else saved step 3 while this editor was open: adopt THEIR
              // version rather than clobbering it, then the user re-applies edits.
              seededFrom.current = serverStep.id;
              setDraft(parseStepPayload(serverStep.payload));
              setSelectedUid(null);
            }}
            continueTo="/onboarding/checklist"
            frozen={frozen}
            completePrimary={false}
          />
        )
      }
    >
      <div className="plant-screen">
        {existing.forbidden ? (
          <Banner kind="neutral" data-testid="plant-existing-forbidden">
            <b>{t("onboarding.plant.existingForbiddenTitle")}</b>{" "}
            {t("onboarding.plant.existingForbiddenBody")}
          </Banner>
        ) : existing.isError ? (
          <ErrorState
            title={t("onboarding.plant.existingErrorTitle")}
            detail={t("onboarding.plant.existingErrorBody")}
            retryLabel={t("onboarding.state.retry")}
            onRetry={existing.refetch}
            requestId={requestIdOf(existing.error)}
            requestIdLabel={t("onboarding.state.requestId")}
          />
        ) : null}

        {/* ---- state 4 · ONE success record, sourced from the 200 body ------ */}
        {lastCommit !== null ? (
          <div className="plant-success" data-testid="plant-commit-success">
            <Banner
              kind="ok"
              data-testid="plant-commit-banner"
              aside={setupChip ?? undefined}
              onDismiss={() => {
                setLastCommit(null);
              }}
              dismissLabel={t("onboarding.plant.dismissBanner")}
            >
              <span className="plant-success-body">
                {/* The reference's headline, with the SERVER's count in it:
                    "Committed 3 nodes — <link>, <link>, <link>." The names come
                    from the client's diff, so they are shown only when the
                    server's own created total agrees; when it does not, the
                    count still stands on its own and no name is claimed. */}
                {createdNamed.length > 0 ? (
                  <NodeLinks
                    label={t("onboarding.plant.committedNodes", { count: createdNamed.length })}
                    nodes={createdNamed}
                    onSelect={selectByPath}
                  />
                ) : serverCreated > 0 ? (
                  <b>{t("onboarding.plant.committedCount", { count: serverCreated })}</b>
                ) : (
                  <b>{t("onboarding.plant.committedTitle")}</b>
                )}{" "}
                <NodeLinks
                  label={t("onboarding.plant.restoredNodes")}
                  nodes={restoredNamed}
                  onSelect={selectByPath}
                />
                <CommitResult result={lastCommit.response} />
              </span>
            </Banner>
            {nextStepCta}
          </div>
        ) : null}

        {contentPending ? (
          /* §5 render-once: which state this screen is IN (guided empty vs
             load-committed vs drafting-with-chips) is decided by the committed
             tree read — and the bind checklist + People panel below decide
             their layout from their own reads — so the WHOLE content region
             gates behind one reserving skeleton and reveals atomically. */
          <CardSkeleton lines={10} label={t("onboarding.plant.existingLoading")} />
        ) : (
          <>
            {isEmptyDraft(draft) ? (
              /* ---- state 1 · guided empty state — ONE claim on screen --------- */
              <Card className="plant-empty-card" data-testid="plant-empty">
                <div className="plant-empty">
                  <ExampleTreeGlyph />
                  <p className="plant-empty-title">{t("onboarding.plant.emptyTitle")}</p>
                  <p className="plant-empty-body">{t("onboarding.plant.emptyBody", nouns)}</p>
                  {canEdit && !hasCommittedTree ? (
                    <Button
                      variant="primary"
                      data-testid="plant-empty-add"
                      onClick={() => {
                        const created = emptyFacility(browserTimezone());
                        setFacilities([created]);
                        setSelectedUid(created.uid);
                      }}
                    >
                      {t("onboarding.plant.emptyCta", nouns)}
                    </Button>
                  ) : null}
                  {hasCommittedTree && canEdit ? (
                    /* A committed plant exists: the one obvious action is to load
                   it, so the admin edits what the factory actually has. */
                    <div className="plant-empty-load" data-testid="plant-load-committed">
                      <p className="plant-empty-body">{t("onboarding.plant.seedBody")}</p>
                      <Button
                        variant="primary"
                        onClick={() => {
                          setDraft(draftFromExisting(existing.tree, locale));
                        }}
                      >
                        {t("onboarding.plant.seedCta")}
                      </Button>
                      <p className="plant-hint">{t("onboarding.plant.seedNote", nouns)}</p>
                    </div>
                  ) : null}
                </div>
              </Card>
            ) : (
              /* ---- states 2/3 · drafting + pre-commit -------------------------- */
              <>
                <div className="ds-toolrow">
                  {canEdit ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        const created = emptyFacility(browserTimezone());
                        setFacilities([...draft.facilities, created]);
                        selectNode(created.uid);
                      }}
                    >
                      {t("onboarding.plant.addFacility", nouns)}
                    </Button>
                  ) : null}
                  <div className="ds-toolrow-right">
                    <StatusChip variant="neutral">{t("onboarding.plant.draftChip")}</StatusChip>
                    <span className="num">
                      {t("onboarding.plant.nodeCount", {
                        count: draftNodeCount,
                        draft: draftOnlyCount,
                      })}
                    </span>
                  </div>
                </div>

                {/* Recorded-not-created, said where it stays true: the commit
                    surface hides as soon as the plant matches, and machines are
                    never a reason to commit (F4 / B1). */}
                <DeferredNote machines={diff.deferred.machines} />

                <div className="ds-pm">
                  <Card
                    title={t("onboarding.plant.treeHeading")}
                    count={draftNodeCount}
                    data-testid="plant-tree-card"
                  >
                    <PlantTree
                      draft={draft}
                      onDraft={setDraft}
                      ctx={ctx}
                      committed={committed}
                      selectedUid={selectedUid}
                      onSelect={selectNode}
                      expanded={expanded}
                      onToggle={(uid) => {
                        setExpanded((prev) => ({
                          ...prev,
                          [uid]: !(prev[uid] ?? defaultExpanded),
                        }));
                      }}
                      defaultExpanded={defaultExpanded}
                      onRemoved={(label, previous) => {
                        setUndo({ draft: previous, label });
                        setSelectedUid(null);
                      }}
                    />
                    {undo !== null ? (
                      <p className="plant-undo" role="status" data-testid="plant-undo">
                        {t("onboarding.plant.removedNotice", { node: undo.label })}{" "}
                        <button
                          type="button"
                          className="ds-lnk"
                          onClick={() => {
                            setDraft(undo.draft);
                            setUndo(null);
                          }}
                        >
                          {t("onboarding.plant.undoRemove")}
                        </button>
                      </p>
                    ) : null}
                  </Card>

                  {selected !== undefined ? (
                    <NodeProperties
                      node={selected}
                      draft={draft}
                      onDraft={setDraft}
                      ctx={ctx}
                      committed={committed}
                      onRemoved={(label, previous) => {
                        setUndo({ draft: previous, label });
                        setSelectedUid(null);
                      }}
                    />
                  ) : (
                    <Card title={t("onboarding.plant.propsEmptyTitle")}>
                      <p className="plant-props-empty">{t("onboarding.plant.propsEmptyBody")}</p>
                    </Card>
                  )}
                </div>

                {errors.length > 0 ? (
                  <div className="plant-problems" role="alert" data-testid="plant-problems">
                    <p className="plant-panel-title">{t("onboarding.plant.problemsTitle")}</p>
                    <ul className="plant-list">
                      {errors.map((p) => (
                        <li key={`${p.code}:${p.path}:${p.fieldId}`}>
                          {/* Each problem FOCUSES the field it is about — selecting
                          the owning node first, since the field lives in the
                          properties panel. */}
                          <button
                            type="button"
                            className="ds-lnk"
                            onClick={() => {
                              focusProblem(p.fieldId);
                            }}
                          >
                            {t(`onboarding.plant.problem.${p.code}`, { ...nouns, path: p.path })}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {warnings.length > 0 || diff.reparented.length > 0 ? (
                  <div className="plant-warnings" data-testid="plant-warnings">
                    <p className="plant-panel-title">{t("onboarding.plant.warningsTitle")}</p>
                    <ul className="plant-list">
                      {warnings.map((p) => (
                        <li key={`${p.code}:${p.path}:${p.fieldId}`}>
                          {t(`onboarding.plant.problem.${p.code}`, { ...nouns, path: p.path })}
                        </li>
                      ))}
                      {diff.reparented.map((node) => (
                        <li key={`moved:${node.path}`}>
                          {t("onboarding.plant.problem.work_center_moved", {
                            ...nouns,
                            path: node.path,
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <CommitPanel
                  draft={draft}
                  diff={diff}
                  committable={isCommittable(problems) && !isEmptyDraft(draft)}
                  canCommit={perms.has("onboarding.manage_wizard")}
                  frozen={frozen}
                  deletionsUnavailable={existing.forbidden || existing.isError}
                  onCommitted={existing.refetch}
                  onSuccess={onCommitSuccess}
                  onDiscard={() => {
                    // Back to the last SAVED draft — the same parse the seeding
                    // effect performs; unsaved edits are what "discard" discards.
                    setDraft(parseStepPayload(step?.payload));
                    setSelectedUid(null);
                  }}
                />
              </>
            )}

            {/* ---- state 5 · bind checklist + people (committed-tree work) --
                PROGRESSIVE DISCLOSURE AT PHONE WIDTHS. These two panels are
                follow-up work on the COMMITTED plant, not part of drafting one;
                stacked open they added ~1.7 k CSS px below the editor, so a
                phone user scrolled past two unrelated panels to reach the
                commit CTA (surface review S5). They open by default on desktop
                — the reference's two-column screen 02 has room for them — and
                collapsed at ≤768 px behind one labelled row that says what is
                inside. Same viewport probe the tree uses, read once at mount so
                a resize never re-collapses a panel under the user's hands. */}
            <div className="plant-followup">
              <button
                type="button"
                className="ds-disclose"
                aria-expanded={followUpOpen}
                data-testid="plant-followup-toggle"
                onClick={() => {
                  setFollowUpOpen((open) => !open);
                }}
              >
                <span className="ds-disclose-car" aria-hidden="true">
                  {followUpOpen ? "▾" : "▸"}
                </span>
                <span className="ds-disclose-lb">{t("onboarding.plant.followUpTitle")}</span>
                <span className="ds-disclose-m">{t("onboarding.plant.followUpHint")}</span>
              </button>
              {followUpOpen ? (
                <>
                  <BindingsPanel
                    tree={existing.tree}
                    templates={stepTemplates.options}
                    noPack={stepTemplates.noPack}
                    frozen={frozen}
                  />

                  <PeoplePanel
                    tree={existing.tree}
                    frozen={frozen}
                    canImportPeople={perms.canImport("people")}
                  />
                </>
              ) : null}
            </div>
          </>
        )}
      </div>
    </StepContainer>
  );
}

/** "New: Стенд-1, Стенд-2." — each name a link that SELECTS the real committed
 * row in the tree, so "did the mapping work?" is answered by clicking. Renders
 * nothing when the list is empty (an uncorroborated list is an empty one). */
function NodeLinks({
  label,
  nodes,
  onSelect,
}: {
  label: string;
  nodes: readonly PlantNodeRef[];
  onSelect: (path: string) => void;
}) {
  if (nodes.length === 0) return null;
  return (
    <span className="plant-success-nodes">
      {label}{" "}
      {nodes.map((node, i) => (
        <span key={node.path}>
          {i > 0 ? ", " : ""}
          <button
            type="button"
            className="ds-lnk"
            data-testid="plant-banner-node"
            data-node-path={node.path}
            onClick={() => {
              onSelect(node.path);
            }}
          >
            {node.name.length > 0 ? node.name : node.path}
          </button>
        </span>
      ))}
      {". "}
    </span>
  );
}

/** A small neutral inline-SVG glyph of the tree the user is about to build —
 * the §3.9 empty state directs with an example, not an illustration program. */
function ExampleTreeGlyph() {
  return (
    <svg className="plant-empty-glyph" viewBox="0 0 96 56" aria-hidden="true">
      <rect x="4" y="6" width="34" height="10" rx="3" />
      <rect x="18" y="24" width="34" height="10" rx="3" />
      <rect x="32" y="42" width="34" height="10" rx="3" />
      <path d="M12 16v13h6M26 34v13h6" fill="none" />
    </svg>
  );
}
